import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Check,
  Star,
  Zap,
  TrendingUp,
  Code2,
  HelpCircle,
} from "lucide-react";
import emailjs from "@emailjs/browser";
import toast from "react-hot-toast";

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/products`; // Change to your backend URL

const ProductDetailPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(0);
  const [expandedSection, setExpandedSection] = useState("overview");
  const [form , setForm] = useState({
    name :  "", 
    email  : "", 
    phone : "",
    date  : "", 
    timeSlot : "",
    message : "",
    productName : ""
  })

  useEffect(() => {
    fetchProduct();
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
  if (product?.heading) {
    setForm((prev) => ({
      ...prev,
      productName: product.heading,
    }));
  }
}, [product]);

  const fetchProduct = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/getbyslug/${slug}`);
      if (!response.ok) throw new Error("Product not found");
      const data = await response.json();
      setProduct(data.data || data);
    } catch (err) {
      setError(err.message);
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
  e.preventDefault();

  // Validation
  if (
    !form.name ||
    !form.email ||
    !form.phone ||
    !form.date ||
    !form.timeSlot ||
    !form.message
  ) {
    toast.error("Please fill all fields");
    return;
  }

  try {
    const templateParams = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      date: form.date,
      timeSlot: form.timeSlot,
      message: form.message,
      productName: form.productName,
    };

    const response = await emailjs.send(
      "YOUR_SERVICE_ID",   // replace
      "YOUR_TEMPLATE_ID",  // replace
      templateParams,
      "YOUR_PUBLIC_KEY"    // replace
    );

    if (response.status === 200) {
      toast.success("Request submitted successfully");

      setForm({
        name: "",
        email: "",
        phone: "",
        date: "",
        timeSlot: "",
        message: "",
        productName: product?.heading || "",
      });
    }
  } catch (error) {
    console.error(error);
    toast.error("Failed to send request");
  }
};

const handleChange = (e) => {
  setForm({
    ...form,
    [e.target.name]: e.target.value,
  });
};

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">
            Loading product details...
          </p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
          <Link
            to="/products"
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8 font-medium transition"
          >
            <ArrowLeft size={20} />
            Back to Products
          </Link>

          <div className="bg-red-100 border-2 border-red-400 text-red-700 px-6 py-4 rounded-lg">
            {error || "Product not found"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Hero Section */}
      <section className=" py-20">
        <div className="bg-gradient-to-r from-orange-200 to-rose-100 py-16">
          <div className="max-w-7xl mx-auto px-8 text-center">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">
              {product.heading}
            </h2>
            <p className="text-xl text-gray-600">{product.tagline}</p>
          </div>
          {/* <div className="flex flex-wrap gap-4">
                <button className="px-8 py-4 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition shadow-lg hover:shadow-xl">
                  Get Started
                </button>
                <button className="px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:bg-opacity-10 transition">
                  Schedule Demo
                </button>
              </div> */}
        </div>
      </section>

      <section>
        <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28">
          {product.metrics && product.metrics.length > 0 && (
            <div className="flex items-center justify-between">
              {product.metrics.map((metric, idx) => (
                <div
                  key={idx}
                  className="p-10 bg-rose-200 bg-opacity-10 backdrop-blur text-center rounded-xl border border-white border-opacity-20 hover:bg-opacity-20 transition"
                >
                  <div className="text-4xl font-bold text-orange-600 mb-2">
                    {metric.value}
                  </div>
                  <div className="text-orange-800 text-sm font-medium">
                    {metric.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left: Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Overview Section */}
            {product.overViewDesc && (
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition border border-gray-100">
                <div className="flex items-start gap-4 mb-6">
                  <div className="p-3 bg-blue-100 rounded-lg flex-shrink-0">
                    <TrendingUp className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">
                      {product.overViewTagLine || "Overview"}
                    </h2>
                  </div>
                </div>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  {product.overViewDesc}
                </p>

                {product.overViewOpints &&
                  product.overViewOpints.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                      {product.overViewOpints.map((point, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className="p-1 bg-green-100 rounded-full flex-shrink-0 mt-1">
                            <Check className="text-green-600" size={16} />
                          </div>
                          <span className="text-gray-700 font-medium">
                            {point}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            )}

            {/* Capabilities Section */}
            {product.capabilities && product.capabilities.length > 0 && (
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition border border-gray-100">
                <div className="flex items-start gap-4 mb-6">
                  <div className="p-3 bg-purple-100 rounded-lg flex-shrink-0">
                    <Zap className="text-purple-600" size={24} />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">
                    Key Capabilities
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {product.capabilities.map((capability, idx) => (
                    <div
                      key={idx}
                      className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-purple-600 rounded-lg flex-shrink-0 mt-1">
                          <Check className="text-white" size={16} />
                        </div>
                        <span className="font-semibold text-gray-900">
                          {capability}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Impact Section */}
            {product.impact && product.impact.length > 0 && (
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200">
                <div className="flex items-start gap-4 mb-6">
                  <div className="p-3 bg-green-100 rounded-lg flex-shrink-0">
                    <Star className="text-green-600" size={24} />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">
                    Business Impact
                  </h2>
                </div>

                <div className="space-y-4">
                  {product.impact.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-4 p-4 bg-white rounded-lg border border-green-200 hover:border-green-400 transition"
                    >
                      <div className="p-2 bg-green-100 rounded-full flex-shrink-0">
                        <Check className="text-green-600" size={20} />
                      </div>
                      <span className="text-gray-900 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Use Cases Section */}
            {product.useCases && product.useCases.length > 0 && (
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition border border-gray-100">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Use Cases
                </h2>

                <div className="space-y-6">
                  {product.useCases.map((useCase, idx) => (
                    <div
                      key={idx}
                      className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 hover:border-blue-400 transition"
                    >
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {useCase.title}
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {useCase.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Get Started Section */}
            {product.getStarted &&
              product.getStarted.steps &&
              product.getStarted.steps.length > 0 && (
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-8 border-2 border-indigo-200">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    {product.getStarted.headline || "Get Started"}
                  </h2>
                  {product.getStarted.subtitle && (
                    <p className="text-gray-600 mb-8">
                      {product.getStarted.subtitle}
                    </p>
                  )}

                  <div className="space-y-4">
                    {product.getStarted.steps.map((step, idx) => (
                      <div key={idx} className="flex gap-6 group">
                        <div className="flex flex-col items-center">
                          <div className="w-12 h-12 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-lg group-hover:bg-indigo-700 transition shadow-lg">
                            {idx + 1}
                          </div>
                          {idx < product.getStarted.steps.length - 1 && (
                            <div className="w-1 h-12 bg-indigo-300 my-2"></div>
                          )}
                        </div>
                        <div className="pb-6">
                          <div className="text-sm font-semibold text-indigo-600 uppercase tracking-wide">
                            {step.num}
                          </div>
                          <h4 className="text-xl font-bold text-gray-900 mt-1">
                            {step.title}
                          </h4>
                          <p className="text-gray-600 mt-2">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Why Choose Section */}
            {product.whyChoose &&
              product.whyChoose.items &&
              product.whyChoose.items.length > 0 && (
                <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition border border-gray-100">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    {product.whyChoose.headline || "Why Choose Us"}
                  </h2>
                  {product.whyChoose.subtitle && (
                    <p className="text-gray-600 mb-8">
                      {product.whyChoose.subtitle}
                    </p>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {product.whyChoose.items.map((item, idx) => {
                      const bgColor =
                        item.variant === "accent"
                          ? "from-orange-50 to-red-50 border-orange-200"
                          : "from-teal-50 to-cyan-50 border-teal-200";

                      return (
                        <div
                          key={idx}
                          className={`p-6 bg-gradient-to-br ${bgColor} rounded-xl border-2 hover:shadow-lg transition`}
                        >
                          <h3 className="text-lg font-bold text-gray-900 mb-3">
                            {item.title}
                          </h3>
                          <p className="text-gray-700">{item.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {/* Platform Highlight Section */}
            {product.platformHighlight &&
              product.platformHighlight.headline && (
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-8 overflow-hidden relative">
                  <div className="absolute inset-0 opacity-5">
                    <Code2 className="absolute top-10 right-10" size={200} />
                  </div>

                  <div className="relative z-10">
                    <h2 className="text-3xl font-bold mb-2">
                      {product.platformHighlight.headline}
                    </h2>
                    {product.platformHighlight.subheadline && (
                      <p className="text-slate-300 mb-6 text-lg">
                        {product.platformHighlight.subheadline}
                      </p>
                    )}

                    {product.platformHighlight.description && (
                      <p className="text-slate-200 mb-8 leading-relaxed">
                        {product.platformHighlight.description}
                      </p>
                    )}

                    {product.platformHighlight.bullets &&
                      product.platformHighlight.bullets.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                          {product.platformHighlight.bullets.map(
                            (bullet, idx) => (
                              <div key={idx} className="flex items-start gap-3">
                                <Check className="text-green-400 flex-shrink-0 mt-1" />
                                <span className="text-slate-100">{bullet}</span>
                              </div>
                            ),
                          )}
                        </div>
                      )}

                    {product.platformHighlight.stats &&
                      product.platformHighlight.stats.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {product.platformHighlight.stats.map((stat, idx) => (
                            <div
                              key={idx}
                              className="p-4 bg-white bg-opacity-10 rounded-lg border border-white border-opacity-20 text-center"
                            >
                              <div className="text-2xl font-bold text-green-400 mb-1">
                                {stat.value}
                              </div>
                              <div className="text-sm text-slate-300">
                                {stat.label}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
              )}

            {/* FAQ Section */}
            {product.faq && product.faq.length > 0 && (
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition border border-gray-100">
                <div className="flex items-start gap-4 mb-8">
                  <div className="p-3 bg-amber-100 rounded-lg flex-shrink-0">
                    <HelpCircle className="text-amber-600" size={24} />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">
                    Frequently Asked Questions
                  </h2>
                </div>

                <div className="space-y-4">
                  {product.faq.map((faqItem, idx) => (
                    <div
                      key={idx}
                      className="border-2 border-gray-200 rounded-xl overflow-hidden hover:border-blue-400 transition"
                    >
                      <button
                        onClick={() =>
                          setExpandedFaq(expandedFaq === idx ? -1 : idx)
                        }
                        className="w-full px-6 py-4 text-left font-bold text-gray-900 hover:bg-gray-50 transition flex items-center justify-between bg-gray-50"
                      >
                        <span>{faqItem.question}</span>
                        {expandedFaq === idx ? (
                          <ChevronUp className="text-blue-600" />
                        ) : (
                          <ChevronDown className="text-gray-600" />
                        )}
                      </button>

                      {expandedFaq === idx && (
                        <div className="px-6 py-4 border-t border-gray-200 bg-white">
                          <p className="text-gray-700 leading-relaxed">
                            {faqItem.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Sidebar */}
          <div className="lg:col-span-1">
            {/* Sticky CTA Card */}
            <div className="top-24 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>

              <p className="text-blue-100 mb-6 leading-relaxed">
                Transform your business with {product.heading}
              </p>

              <form onSubmit={handleFormSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 rounded-lg bg-white text-gray-800 outline-none"
                  />
                </div>

                {/* Email */}
                <div>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 rounded-lg bg-white text-gray-800 outline-none"
                  />
                </div>

                {/* Phone */}
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                    className="w-full px-4 py-3 rounded-lg bg-white text-gray-800 outline-none"
                  />
                </div>

                {/* Date */}
                <div>
                  <input
                    type="date"
                    name="date"
                  value={form.date}
                  onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-white text-gray-800 outline-none"
                  />
                </div>

                {/* Time Slot */}
                <div>
                  <select value={form.timeSlot} onChange={handleChange} className="w-full px-4 py-3 rounded-lg bg-white text-gray-800 outline-none">
                    <option value="">Select Time Slot</option>
                    <option>09:00 AM - 10:00 AM</option>
                    <option>10:00 AM - 11:00 AM</option>
                    <option>11:00 AM - 12:00 PM</option>
                    <option>01:00 PM - 02:00 PM</option>
                    <option>02:00 PM - 03:00 PM</option>
                    <option>03:00 PM - 04:00 PM</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <textarea
                    rows="4"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Write your message..."
                    className="w-full px-4 py-3 rounded-lg bg-white text-gray-800 outline-none resize-none"
                  />
                </div>

                {/* Buttons */}
                <div className="space-y-3">
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition shadow-lg hover:shadow-xl"
                  >
                    Request Demo
                  </button>

                  <Link
                    to="/contact"
                    type="button"
                    onClick={()=> window.scrollTo({top :0 , behavior : "smooth"})}
                    className="inline-block text-center w-full px-6 py-3 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:bg-opacity-10 transition"
                  >
                    Get Exports Advice
                  </Link>
                </div>
              </form>

              {/* Benefits */}
              <div className="border-t border-blue-400 pt-6 mt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Check className="flex-shrink-0 mt-1" size={20} />
                  <div>
                    <div className="font-bold">7-day free trial</div>
                    <div className="text-sm text-blue-100">No credit card</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Check className="flex-shrink-0 mt-1" size={20} />
                  <div>
                    <div className="font-bold">24/7 Support</div>
                    <div className="text-sm text-blue-100">
                      Always available
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Check className="flex-shrink-0 mt-1" size={20} />
                  <div>
                    <div className="font-bold">Enterprise Ready</div>
                    <div className="text-sm text-blue-100">
                      Scalable solution
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Info Card */}
            <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h4 className="font-bold text-gray-900 mb-4">Product Info</h4>

              <div className="space-y-4 text-sm">
                <div>
                  <div className="text-gray-600 font-medium mb-1">Status</div>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                      product.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {product.isActive ? "Active" : "Inactive"}
                  </span>
                </div>

                <div>
                  <div className="text-gray-600 font-medium mb-2">
                    Capabilities
                  </div>
                  <div className="text-gray-700">
                    {product.capabilities?.length || 0} capabilities
                  </div>
                </div>

                <div>
                  <div className="text-gray-600 font-medium mb-2">
                    Integrations
                  </div>
                  <div className="text-gray-700">
                    {product.integrations?.length || 0} integrations
                  </div>
                </div>

                <div>
                  <div className="text-gray-600 font-medium mb-2">
                    Use Cases
                  </div>
                  <div className="text-gray-700">
                    {product.useCases?.length || 0} use cases
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related Products CTA */}
      <section className="bg-gradient-to-r from-blue-300 to-indigo-600 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Explore Our Industries</h2>
          <p className="text-xl text-blue-100 mb-8">
            Discover more products with related Industries
          </p>
          <Link
            to="/industry"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition shadow-lg hover:shadow-xl"
          >
            View All Industries →
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductDetailPage;
